import { HourlyAccountConfigFeatureEnum } from 'parkchirp-shared/enums';
import { FC, useCallback, useMemo } from 'react';
import { HourlyAccountConfig, HourlyAccountUserGroup } from '../../types';
import { formatCamelcaseToEnumValue } from '../../utils';
import { StartSessionConfigEditor } from './configEditors/startSessionConfigEditor';

export interface PortalConfigsEditorProps {
	readonlyConfigs?: HourlyAccountConfig[];
	facilityId?: string;
	isParentLoading?: boolean;
	isPageEditing?: boolean;
	isAccountConfigEditing?: boolean;
	setAccountConfigEditing: (editing: boolean) => void;
	accountId?: string;
	refetchHourlyAccount: () => void;
	readonlyPrimaryUserGroup?: HourlyAccountUserGroup;
}

const CONFIGURABLE_FEATURES: HourlyAccountConfigFeatureEnum[] = [HourlyAccountConfigFeatureEnum.START_SESSION];

export const PortalConfigsEditor: FC<PortalConfigsEditorProps> = ({
	readonlyConfigs = [],
	readonlyPrimaryUserGroup,
	facilityId,
	isParentLoading,
	accountId,
	isPageEditing,
	isAccountConfigEditing,
	setAccountConfigEditing,
	refetchHourlyAccount,
}) => {
	const enabledFunctionGroupNames = useMemo(
		() => readonlyPrimaryUserGroup?.userGroup?.functionGroups.map((fg) => fg.functionGroup.name) || [],
		[readonlyPrimaryUserGroup],
	);

	const enabledFeatures = useMemo(
		() =>
			enabledFunctionGroupNames.reduce((features, functionGroupName) => {
				const featureName = formatCamelcaseToEnumValue(functionGroupName) as HourlyAccountConfigFeatureEnum;

				if (CONFIGURABLE_FEATURES.includes(featureName)) {
					features[featureName] = true;
				}

				return features;
			}, {}),
		[enabledFunctionGroupNames],
	);

	const isFeatureEnabled = useCallback(
		(featureName: HourlyAccountConfigFeatureEnum) => {
			return enabledFeatures[featureName];
		},
		[enabledFeatures],
	);

	return (
		<>
			{isFeatureEnabled(HourlyAccountConfigFeatureEnum.START_SESSION) && (
				<StartSessionConfigEditor
					readonlyConfigs={readonlyConfigs}
					facilityId={facilityId}
					isPageEditing={isPageEditing}
					isParentLoading={isParentLoading}
					accountId={accountId}
					refetchHourlyAccount={refetchHourlyAccount}
					isAccountConfigEditing={isAccountConfigEditing}
					setAccountConfigEditing={setAccountConfigEditing}
				/>
			)}
			{!isParentLoading && !Object.keys(enabledFeatures).length && <p>No configurable features enabled</p>}
		</>
	);
};
